/*
 * Model for conversation variables
 */

define([
  "backbone",
  "jquery",
  "messaging/messenger",
  "configuration/properties",
  "modules/shared/sortableList/sortableListModel",
], function (Backbone, $, Messenger, Properties, SortableListModel) {
  "use strict";

  const ConversationVariableModel = {};

  ConversationVariableModel.conversationVariable = Backbone.Model.extend({
    defaults: {
      name: "",
      description: "",
    },
    defaultValidateAttributes: ["name", "description"],
    idAttribute: "variableId",
    //variable should return and update prompts for the given promptType when calling getResponse(), setResponse()
    selectedPromptType: 0,
    MISSING_PROMPT_TYPE: 0,
    INVALID_PROMPT_TYPE: 1,
    prompts: function (options) {
      const modelOptions = Object.assign(
        {
          modelClass: ConversationVariableModel.variablePrompts,
          urlExtension: "/prompts",
        },
        options,
      );
      this.promptsCol = this.getSubModel2(this.promptsCol, modelOptions);
      return this.promptsCol;
    },
    setSelectedPromptType: function (promptType) {
      this.selectedPromptType = promptType;
    },
    hasPrompts: function (promptType, callback, context) {
      const prompts = this.prompts();
      prompts.onSynced(() => {
        const hasPrompts = prompts.findWhere({ promptType: promptType }) !== undefined;
        callback.call(context || this, hasPrompts);
      }, this);
    },
    //Gets the prompt for the given TP and locale.  Once found, it evaluates the callback with the prompt text as a parameter.
    //If no response is found, returns an empty string.
    getResponse: function (touchPointId, localeId, callback, context) {
      const prompts = this.prompts();
      prompts.onSynced(() => {
        let promptText = "";
        const prompt = prompts.findWhere({
          touchPointId: touchPointId,
          localeId: localeId,
          promptType: this.selectedPromptType,
        });
        if (prompt) {
          promptText = prompt.getPromptText();
        }
        callback.call(context || this, promptText);
      });
    },
    setResponse: function (touchPointId, localeId, responseText) {
      const prompts = this.prompts();
      prompts.onSynced(() => {
        let prompt = prompts.findWhere({
          touchPointId: touchPointId,
          localeId: localeId,
          promptType: this.selectedPromptType,
        });
        if (!prompt) {
          //create new prompt and add to the collection
          prompt = prompts.add({
            touchPointId: touchPointId,
            localeId: localeId,
            promptType: this.selectedPromptType,
            variableId: this.id,
          });
        }
        prompt.setPromptText(responseText);
      });
    },
    getValidationConstraints: function (callback, context) {
      //check if the variable is defined already so we don't apply the same invalid handler multiple times
      const previouslyDefined = this.validationConstraints !== undefined;
      this.validationConstraints = this.getSubModel(
        this.validationConstraints,
        ConversationVariableModel.validationConstraint,
        "/constraints",
      );

      //any invalid events on the child object should propagate to the parent
      if (!previouslyDefined) {
        this.validationConstraints.on(
          "invalid",
          function (_model, error) {
            this.trigger("invalid", this, error);
          },
          this,
        );
      }

      this.validationConstraints.onSynced(function () {
        callback.apply(context, [this.validationConstraints]);
      }, this);
    },
    _validateDuplicateVariableName: function (value) {
      for (let i = 0; i < this.collection.length; i++) {
        const variable = this.collection.at(i);
        if (this !== variable) {
          //don't check the entry against itself
          if (value.toUpperCase() === variable.get("name").toUpperCase()) {
            return $.t("admin.conversationVariable.validationError");
          }
        }
      }
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "name") {
        return (
          this.validateRequiredValue(value, { maxLength: 100 }) ||
          this.validateSafeCharacters(value) ||
          this._validateDuplicateVariableName(value)
        );
      }
      if (attribute === "description") {
        return this.validateStringLength(value, { maxLength: 1000 });
      }
    },
    _createSaveModelSuccessWithChildren: function (options) {
      return (model) => {
        model.dirty = false;
        model.validationConstraints.setParentUrl(model.url());
        model.saveChildren(options);
      };
    },
    saveChildren: function (options) {
      //pass all the data needed to the child models for saving
      this.saveSubModels(options);
    },
    //define the URL root if the model is loaded outside of a collection
    urlRoot: function () {
      const kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/variables";
    },
  });

  ConversationVariableModel.conversationVariables = Backbone.Collection.extend({
    model: ConversationVariableModel.conversationVariable,
    addItem: function () {
      const newItem = new ConversationVariableModel.conversationVariable();
      this.add(newItem);
      return newItem;
    },
    comparator: function (a, b) {
      return Backbone.textComparator(a, b, "name");
    },
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/variables";
    },
  });

  ConversationVariableModel.reservedVariables = Backbone.Collection.extend({
    model: ConversationVariableModel.conversationVariable,
    url: function () {
      const kbId = Messenger.reqres.request("active:kb");
      return Properties.rootURL + "/" + kbId + "/variables/reserved";
    },
  });

  ConversationVariableModel.variablePrompt = Backbone.Model.extend({
    idAttribute: "templateId",
    defaults: {
      prompt: "",
    },
    getPromptText: function () {
      let promptText = this.get("prompt");
      if (promptText) {
        const privilegedAreaRoles = Messenger.reqres.request("kb:privileged:area:roles");
        privilegedAreaRoles.onSynced(function () {
          const promptHTML = $(promptText);
          promptHTML.find("div[data-rdprivileges]").each(function () {
            //get the roleIds of the roles used in this area
            const areaRolesArray = $(this).attr("data-rdprivileges").split(",");
            //create the list of role names
            let roleNames = "";
            for (let i = 0; i < areaRolesArray.length; i++) {
              const roleMatch = privilegedAreaRoles.findWhere({
                roleId: parseInt(areaRolesArray[i].trim(), 10),
              });
              if (roleMatch) {
                roleNames += roleMatch.get("roleName") + ", ";
              } else {
                roleNames += $.t("privilegedArea.invalidRole");
              }
            }
            roleNames = roleNames.slice(0, -2); //slice out last comma
            //set the names list as an attribute of the privileged area div
            $(this).attr("data-rdprivileges-name", roleNames);
          });
          promptText = promptHTML[0].outerHTML;
        });
      }
      return promptText;
    },
    setPromptText: function (promptText) {
      const promptHTML = $(promptText);
      promptHTML.find("div[data-rdprivileges]").each(function () {
        //remove the attribute data-rdprivileges-name
        $(this).removeAttr("data-rdprivileges-name");
      });
      promptText = promptHTML[0].outerHTML;
      if (this.get("prompt") !== promptText) {
        this.set("prompt", promptText);
      }
    },
  });

  ConversationVariableModel.variablePrompts = Backbone.Collection.extend({
    model: ConversationVariableModel.variablePrompt,
  });

  ConversationVariableModel.validationConstraint = Backbone.Model.extend({
    id: "",
    defaults: {
      constraintType: 0,
    },
    setParentUrl: function (parentUrl) {
      this.urlRoot = parentUrl + "/constraints";
    },
    setType: function (constraintType) {
      this.set("constraintType", constraintType);
      //clear out the subgroup constraint if there is no constraint and a sub group constraint is defined
      if (constraintType !== 1 && this.get("subGroupConstraint")) {
        this.set("subGroupConstraint", null);
      }
      if (constraintType !== 2 && this.get("patternConstraint")) {
        this.set("patternConstraint", null);
      }
    },
    validate: function () {
      const error = {};
      let isValid = true;

      if (this.get("constraintType") === 0) {
        // Validate constraint type of none
        if (this.get("subGroupConstraint")) {
          isValid = false;
          error["validation"] = $.t("admin.conversationVariable.nonEmptySubGroup");
        }
      } else if (this.get("constraintType") === 1) {
        // Validate constraint type of sub group
        if (!this.get("subGroupConstraint")) {
          isValid = false;
          error["validation"] = $.t("admin.conversationVariable.invalidSubstitutionGroup");
        }
      } else if (this.get("constraintType") === 2) {
        // Validate constraint type of pattern
        if (!this.get("patternConstraint")) {
          isValid = false;
          error["validation"] = $.t("admin.conversationVariable.invalidPattern");
        } else {
          const pattern = this.get("patternConstraint")["pattern"];
          const patternError = this.validateRequiredValue(pattern, { maxLength: 255 });
          if (patternError) {
            isValid = false;
            error["validation"] = patternError;
          }
        }
      } else {
        // Invalid constraint type
        isValid = false;
        error["validation"] = $.t("admin.conversationVariable.invalidValidationType");
      }

      if (!isValid) {
        return error;
      }
    },
  });

  Messenger.reqres.setHandler("conversation:variables", function () {
    const convVars = new ConversationVariableModel.conversationVariables();
    convVars.on("sync", function () {
      this.sort();
    });
    convVars.fetch();
    return convVars;
  });

  Messenger.reqres.setHandler("reserved:variables", function () {
    const convVars = new ConversationVariableModel.reservedVariables();
    convVars.fetch();
    return convVars;
  });

  Messenger.reqres.setHandler("all:variables", function () {
    //create a collection that merges the two collections together as if they were fetched from the server
    const variables = Messenger.reqres.request("conversation:variables");
    const reservedVariables = Messenger.reqres.request("reserved:variables");
    const allVariables = new ConversationVariableModel.conversationVariables();
    allVariables.trigger("request");
    variables.onSynced(function () {
      reservedVariables.onSynced(function () {
        allVariables.add(variables.models, { silent: true });
        reservedVariables.each(function (variable) {
          variable.readOnly = true;
        });
        allVariables.add(reservedVariables.models, { silent: true });
        allVariables.trigger("fetched");
        allVariables.trigger("sync");
      }, this);
    }, this);
    return allVariables;
  });

  /**
   * Get a single conversation variable by its ID
   */
  Messenger.reqres.setHandler("conversation:variable", function (variableId) {
    const variable = new ConversationVariableModel.conversationVariable({ variableId: variableId });
    variable.fetch();
    return variable;
  });

  Messenger.reqres.setHandler(
    "conversationVariables:config:headers",
    function () {
      const Header = SortableListModel.headerModel;
      return new Backbone.Collection([
        new Header({
          headerName: $.t("admin.conversationVariable.name"),
          modelAttribute: "name",
          selectable: true,
          sortable: true,
          filterable: false,
        }),
        new Header({
          headerName: $.t("admin.conversationVariable.description"),
          modelAttribute: "description",
          selectable: false,
          sortable: false,
          filterable: false,
        }),
        new Header({
          headerName: "",
          modelAttribute: "name",
          width: "15px",
          customHtml:
            '<button id="deleteConversationVariable" type="button" class="btn btn-danger btn-mini" data-toggle="tooltip" data-i18n="[title]admin.conversationVariable.deleteConversationVariable"> \n\
            <i class="icon-remove"></i> \n\
          </button> ',
        }),
      ]);
    },
    this,
  );

  return ConversationVariableModel;
});
